<template>
    <div>
        <DataTable
            :header="$t('deviationsFromRecommendedPrice')"
            :fields="fields"
            :items="chartConfigurations"
            :loading="request.getLoading()"
            :hover="false"
            class="mb-4"
            thead-class="d-none"
            table-class="table-layout-fixed"
            :overflow="false"
        >
            <template #cell(chart)="{ item }">
                <div class="position-relative">
                    <ButtonIcon
                        class="edit-btn wide"
                        size="sm"
                        :disabled="requestUpdatePrice.getLoading(item.product.id)"
                        icon="edit-2"
                        @click="openEditModal(item.product)"
                    >
                        {{ item.product.price != null ? $format.currency(item.product.price) : $t('giveThePrice') }}
                    </ButtonIcon>
                    <EChart :options="item.chartOptions" />
                </div>
            </template>
        </DataTable>
        <ModalEditRecommendedPrice />
    </div>
</template>



<script>
    import EChart from '@/components/common/EChart.vue';
    import DataTable from '@/components/common/DataTable.vue';
    import ButtonIcon from '@/components/common/ButtonIcon.vue';
    import ModalEditRecommendedPrice from './ModalEditRecommendedPrice.vue';

    export default {
        name: 'DeviationsFromRecommendedPrice',
        components: {
            EChart,
            DataTable,
            ButtonIcon,
            ModalEditRecommendedPrice,
        },
        inject: {
            parentComponent: {
                default: null,
            },
        },
        provide() {
            return {
                parentComponent: this,
            };
        },
        data() {
            return {
                request: this.parentComponent.requestData,
                requestUpdatePrice: this.parentComponent.requestUpdatePrice,
                currency: process.env.VUE_APP_SYSTEM_CURRENCY,
            };
        },
        computed: {
            fields() {
                return [{ key: 'chart', label: '' }];
            },
            rawProductsData() {
                return this.parentComponent.data?.products || {};
            },
            chartConfigurations() {
                return Object.values(this.rawProductsData).map(({ product, price: entries }) => {
                    const priceEntries = Array.isArray(entries) ? entries : [];
                    const recommendedPrice = product.price;

                    // 1) Zbieramy sklepy i inicjalizujemy statystyki
                    const statsByStore = new Map();
                    for (const entry of priceEntries) {
                        for (const key of Object.keys(entry)) {
                            if (key === 'date') continue; // eslint-disable-line no-continue
                            if (!statsByStore.has(key)) {
                                statsByStore.set(key, {
                                    totalSum: 0,
                                    occurrences: 0,
                                    minimum: Infinity,
                                    maximum: -Infinity,
                                });
                            }
                        }
                    }

                    // 2) Jedna pętla: sumy, min/max, licznik
                    for (const entry of priceEntries) {
                        for (const [storeName, stats] of statsByStore.entries()) {
                            const value = entry[storeName];
                            if (value != null) {
                                stats.totalSum += value;
                                stats.occurrences += 1;
                                stats.minimum = Math.min(stats.minimum, value);
                                stats.maximum = Math.max(stats.maximum, value);
                            }
                        }
                    }

                    // 3) Przygotowujemy dane do wykresu
                    const storeNames = Array.from(statsByStore.keys()).sort();
                    const baseline = storeNames.map(() => recommendedPrice);
                    const scatterData = storeNames.map(storeName => {
                        const { totalSum, occurrences, minimum, maximum } = statsByStore.get(storeName);
                        const averagePrice = occurrences > 0 ? totalSum / occurrences : null;
                        const deviationValue = averagePrice != null && recommendedPrice != null ? averagePrice - recommendedPrice : null;
                        const deviationPercent = deviationValue != null && recommendedPrice !== 0 ? (deviationValue / recommendedPrice) * 100 : null;
                        return {
                            value: averagePrice,
                            storeName,
                            averagePrice,
                            minimumPrice: minimum === Infinity ? null : minimum,
                            maximumPrice: maximum === -Infinity ? null : maximum,
                            deviationValue,
                            deviationPercent,
                            recommendedPrice,
                        };
                    });

                    // 4) Zamrażamy opcje wykresu, żeby Vue2 nie deep-watchowało
                    const chartOptions = Object.freeze({
                        grid: { top: '80px' },
                        title: {
                            text: product.name,
                            subtext: `EAN: ${product.ean}`,
                            textStyle: {
                                fontSize: 14,
                            },
                        },
                        tooltip: {
                            trigger: 'axis',
                            formatter: this.formatDeviationTooltip,
                        },
                        xAxis: {
                            type: 'category',
                            data: storeNames,
                        },
                        yAxis: {
                            type: 'value',
                            axisLabel: {
                                formatter: val => this.$format.currency(val, null, null, 0),
                            },
                        },
                        series: [
                            {
                                name: `${this.$t('recommendedPrice')} [${this.currency}]`,
                                type: 'line',
                                data: baseline,
                                lineStyle: { color: 'black' },
                                itemStyle: { color: 'black' },
                                symbol: 'none',
                                areaStyle: null,
                            },
                            {
                                name: `${this.$t('deviation')} %`,
                                type: 'scatter',
                                data: scatterData,
                            },
                        ],
                    });

                    return { product, chartOptions };
                });
            },
        },
        methods: {
            formatDeviationTooltip(items) {
                const scatterItem = items.find(item => item.seriesType === 'scatter');
                if (!scatterItem || !scatterItem.data) {
                    return '';
                }
                const pointData = scatterItem.data;
                const formatCurrency = amount => (amount != null ? this.$format.currency(amount) : '-');
                const formatPercent = amount => (amount != null ? this.$format.percent(amount) : '-');
                const deviationText =
                    pointData.deviationValue != null ? `${pointData.deviationPercent >= 0 ? '+' : ''}${formatPercent(pointData.deviationPercent)} (${pointData.deviationValue >= 0 ? '+' : ''}${formatCurrency(pointData.deviationValue)})` : '-';

                return `
                    <div style="display:flex;flex-direction:column;gap:3px;">
                        <div style="margin-bottom:2px;">${scatterItem.marker}<span style="margin-left:2px">${scatterItem.name}</span></div>
                        <div style="display:flex;gap:20px;">${this.$t('price')}: <strong style="margin-left:auto;font-weight:900">${formatCurrency(pointData.averagePrice)}</strong></div>
                        <div style="display:flex;gap:20px;">${this.$t('recommendedPrice')}: <strong style="margin-left:auto;font-weight:900">${formatCurrency(pointData.recommendedPrice)}</strong></div>
                        <div style="display:flex;gap:20px;">${this.$t('deviation')}: <strong style="margin-left:auto;font-weight:900">${deviationText}</strong></div>
                        <div style="display:flex;gap:20px;">${this.$t('minimumPrice')}: <strong style="margin-left:auto;font-weight:900">${formatCurrency(pointData.minimumPrice)}</strong></div>
                        <div style="display:flex;gap:20px;">${this.$t('maximumPrice')}: <strong style="margin-left:auto;font-weight:900">${formatCurrency(pointData.maximumPrice)}</strong></div>
                    </div>
                    `;
            },
            openEditModal(product) {
                this.$root.$emit('bv::show::modal', 'modal-edit-recommended-price');
                this.$root.$emit('modal-edit-recommended-price', product);
            },
        },
    };
</script>


<style lang="scss" scoped>
    .edit-btn {
        position: absolute;
        right: 10px;
        top: 10px;
        z-index: 10;
    }
</style>
